import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import api from '../api';
import { loadRazorpayCheckout } from '../lib/razorpay';
import { useAuth } from './AuthContext';

const BookingContext = createContext();

export const useBooking = () => {
  const context = useContext(BookingContext);
  if (!context) {
    throw new Error('useBooking must be used within BookingProvider');
  }
  return context;
};

// Backend snake_case -> existing UI camelCase shape
const mapBooking = (b) =>
  b && {
    id: b.id,
    userId: b.user_id,
    driverId: b.driver_id,
    driverPublicId: b.driver_public_id,
    driverName: b.driver_name,
    driverPhone: b.driver_phone,
    status: b.status,
    hours: b.hours,
    baseAmount: b.base_amount,
    discount: b.discount,
    lateNightCharge: b.late_night_charge,
    amount: b.amount,
    pickupLocation: b.pickup_location,
    dropLocation: b.drop_location,
    pickupLat: b.pickup_lat,
    pickupLng: b.pickup_lng,
    dropLat: b.drop_lat,
    dropLng: b.drop_lng,
    routeDistanceKm: b.route_distance_km,
    routeDurationMin: b.route_duration_min,
    femaleDriverOnly: b.female_driver_only,
    searchRadiusKm: b.search_radius_km,
    searchStartedAt: b.search_started_at,
    searchExpiresAt: b.search_expires_at,
    searchEndedReason: b.search_ended_reason,
    retryCount: b.retry_count,
    nearbyDriverCount: b.nearby_driver_count,
    startPin: b.start_pin,
    driverProfile: b.driver_profile
      ? {
          driverId: b.driver_profile.driver_id,
          fullName: b.driver_profile.full_name,
          rating: b.driver_profile.rating,
          totalTrips: b.driver_profile.total_trips,
          experienceYears: b.driver_profile.experience_years,
          gender: b.driver_profile.gender,
          photoUrl: b.driver_profile.photo_url,
        }
      : null,
    carType: b.car_type,
    carTypeName: b.car_type_name,
    bookingTime: b.booking_time,
    scheduledDate: b.scheduled_date,
    scheduledTime: b.scheduled_time,
    couponCode: b.coupon_code,
    rating: b.rating,
    review: b.review,
    paymentStatus: b.payment_status,
    paymentMethod: b.payment_method,
    cashConfirmedByDriver: b.cash_confirmed_by_driver,
    cashConfirmedByUser: b.cash_confirmed_by_user,
    razorpayPaymentId: b.razorpay_payment_id,
    refundId: b.refund_id,
    createdAt: b.created_at,
    assignedAt: b.assigned_at,
    completedAt: b.completed_at,
    cancelledAt: b.cancelled_at,
  };

export const BookingProvider = ({ children }) => {
  const { isAuthenticated, userType, refreshUser } = useAuth();
  const [activeBooking, setActiveBooking] = useState(null);
  const [bookingHistory, setBookingHistory] = useState([]);
  const [pendingBookings, setPendingBookings] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch active booking + history when a USER logs in
  useEffect(() => {
    if (!isAuthenticated || userType !== 'user') {
      setActiveBooking(null);
      setBookingHistory([]);
      return;
    }
    const load = async () => {
      setLoading(true);
      try {
        const [activeResp, histResp] = await Promise.all([
          api.get('/api/bookings/active'),
          api.get('/api/bookings/history'),
        ]);
        setActiveBooking(mapBooking(activeResp.data));
        setBookingHistory((histResp.data || []).map(mapBooking));
      } catch {
        /* ignore — likely 401 */
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isAuthenticated, userType]);

  // Fetch the active booking right now (used by the poller and by the WS
  // status-change listener for an instant refresh instead of waiting on the timer).
  const refreshActiveBooking = useCallback(async () => {
    try {
      const { data } = await api.get('/api/bookings/active');
      if (data) {
        setActiveBooking(mapBooking(data));
      } else {
        setActiveBooking((prev) =>
          prev && !['completed', 'cancelled'].includes(prev.status) ? { ...prev, status: 'completed' } : prev
        );
        try {
          const hist = await api.get('/api/bookings/history');
          setBookingHistory((hist.data || []).map(mapBooking));
        } catch { /* ignore */ }
      }
    } catch {
      /* ignore */
    }
  }, []);

  // Poll the active booking as a safety net (WS is the primary path). Poll faster
  // while searching so the 90s timeout / no-drivers state shows promptly.
  useEffect(() => {
    if (!isAuthenticated || userType !== 'user') return;
    if (!activeBooking) return;
    if (['completed', 'cancelled'].includes(activeBooking.status)) return;

    const interval = setInterval(refreshActiveBooking, activeBooking.status === 'searching' ? 4000 : 8000);
    return () => clearInterval(interval);
  }, [activeBooking, isAuthenticated, userType, refreshActiveBooking]);

  const createBooking = useCallback(async (payload) => {
    // Booking is created unpaid and starts the driver search immediately.
    // Payment is collected separately via payForBooking() once a driver accepts.
    const body = {
      hours: payload.hours,
      pickup_location: payload.pickupLocation,
      drop_location: payload.dropLocation,
      booking_time: payload.bookingTime || 'now',
      scheduled_date: payload.scheduledDate || null,
      scheduled_time: payload.scheduledTime || null,
      coupon_code: payload.couponCode || null,
      pickup_lat: payload.pickupLat ?? null,
      pickup_lng: payload.pickupLng ?? null,
      drop_lat: payload.dropLat ?? null,
      drop_lng: payload.dropLng ?? null,
      route_distance_km: payload.routeDistanceKm ?? null,
      route_duration_min: payload.routeDurationMin ?? null,
      female_driver_only: !!payload.femaleDriverOnly,
    };
    const { data } = await api.post('/api/bookings', body);
    const mapped = mapBooking(data);
    setActiveBooking(mapped);
    return mapped;
  }, []);

  const choosePayCash = useCallback(async (bookingId) => {
    const { data } = await api.post(`/api/bookings/${bookingId}/payment/cash`);
    const mapped = mapBooking(data);
    setActiveBooking(mapped);
    return mapped;
  }, []);

  const confirmCashPaid = useCallback(async (bookingId) => {
    const { data } = await api.post(`/api/bookings/${bookingId}/cash/confirm`);
    const mapped = mapBooking(data);
    setActiveBooking(mapped);
    return mapped;
  }, []);

  // Opens Razorpay checkout for an already-assigned booking's locked-in amount.
  // Called once the user sees "Driver Assigned" and taps "Pay Now".
  const payForBooking = useCallback(async (bookingId, user) => {
    const loaded = await loadRazorpayCheckout();
    if (!loaded) {
      throw new Error('Could not load Razorpay');
    }

    const { data: order } = await api.post(`/api/bookings/${bookingId}/payment/create-order`);

    // Best-effort: prepare a Razorpay Customer so the checkout can offer
    // "save this card" for next time. Never blocks payment if it fails.
    let customerId = null;
    try {
      const { data: pm } = await api.post('/api/users/payment-methods/setup');
      customerId = pm.customer_id;
    } catch { /* saved-card setup is optional */ }

    return new Promise((resolve, reject) => {
      const options = {
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: 'BookMyDriver',
        description: 'Driver booking payment',
        order_id: order.order_id,
        ...(customerId ? { customer_id: customerId, save: 1 } : {}),
        prefill: {
          name: user?.name || 'BookMyDriver User',
          contact: user?.phone || '',
        },
        notes: { purpose: 'trip_booking', booking_id: bookingId },
        theme: { color: '#000000' },
        handler: async (response) => {
          try {
            const { data: booking } = await api.post(`/api/bookings/${bookingId}/payment/verify`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            const mapped = mapBooking(booking);
            setActiveBooking(mapped);
            refreshUser(); // pulls fresh wallet + clears a consumed referral discount
            resolve(mapped);
          } catch (err) {
            reject(err);
          }
        },
        modal: {
          ondismiss: () => reject(new Error('Payment cancelled')),
        },
      };
      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (resp) => reject(new Error(resp?.error?.description || 'Payment failed')));
      rzp.open();
    });
  }, [refreshUser]);

  const cancelBooking = useCallback(async (bookingId) => {
    const { data } = await api.post(`/api/bookings/${bookingId}/cancel`);
    setActiveBooking(null);
    try {
      const hist = await api.get('/api/bookings/history');
      setBookingHistory((hist.data || []).map(mapBooking));
    } catch { /* ignore */ }
    return mapBooking(data);
  }, []);

  // Restart the driver search on the same paid booking (radius expands to 10 km)
  const retrySearch = useCallback(async (bookingId) => {
    const { data } = await api.post(`/api/bookings/${bookingId}/retry-search`);
    const mapped = mapBooking(data);
    setActiveBooking(mapped);
    return mapped;
  }, []);

  // Legacy signature kept for ActiveBooking.js — currently used for cancel only
  const updateBookingStatus = useCallback(async (bookingId, status) => {
    if (status === 'cancelled') {
      return await cancelBooking(bookingId);
    }
    return null;
  }, [cancelBooking]);

  const addRating = useCallback(async (bookingId, rating, review) => {
    await api.post(`/api/bookings/${bookingId}/rate`, { rating, review });
    try {
      const { data } = await api.get('/api/bookings/history');
      setBookingHistory((data || []).map(mapBooking));
    } catch { /* ignore */ }
  }, []);

  // Driver-side pending list helpers (UI calls these; provider just exposes setters)
  const addPendingBooking = useCallback((booking) => {
    setPendingBookings((prev) => [...prev, booking]);
  }, []);
  const removePendingBooking = useCallback((bookingId) => {
    setPendingBookings((prev) => prev.filter((b) => b.id !== bookingId));
  }, []);
  const setAllPendingBookings = useCallback((list) => {
    setPendingBookings(list);
  }, []);

  const value = useMemo(
    () => ({
      activeBooking,
      bookingHistory,
      pendingBookings,
      loading,
      createBooking,
      payForBooking,
      choosePayCash,
      confirmCashPaid,
      cancelBooking,
      retrySearch,
      updateBookingStatus,
      addRating,
      addPendingBooking,
      removePendingBooking,
      setAllPendingBookings,
      refreshActiveBooking,
    }),
    [
      activeBooking, bookingHistory, pendingBookings, loading,
      createBooking, payForBooking, choosePayCash, confirmCashPaid, cancelBooking, retrySearch, updateBookingStatus, addRating,
      addPendingBooking, removePendingBooking, setAllPendingBookings, refreshActiveBooking,
    ]
  );

  return <BookingContext.Provider value={value}>{children}</BookingContext.Provider>;
};
